type SpokenNumber = {
  value: number;
  text?: string;
  isMoney: boolean;
};

const UNITS: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
};

const TEENS: Record<string, number> = {
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};

const TENS: Record<string, number> = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

const MULTIPLIERS: Record<string, number> = { hundred: 100, thousand: 1000 };
const ARTICLES = new Set(['a', 'an']);
const MONEY_WORDS = new Set(['dollar', 'dollars', 'buck', 'bucks', 'usd']);
const NEGATIONS = new Set(['minus', 'negative']);
/** "this one", "the one": `one` as a pronoun, not a count. */
const PRONOUN_ONE_DETERMINERS = new Set(['this', 'that', 'the', 'which', 'each', 'every', 'another', 'any', 'no']);

const PLAIN_DIGITS = /^\$?\d[\d,]*(?:\.\d+)?$/;

/**
 * Words and digit runs in spoken order. A token mixing digits with letters or symbols (`2x4`,
 * `r13`, `1/2`, `3-light`) stays whole so it reads as a product spec, not a quantity; hyphenated
 * words ("twenty-one") split into their words.
 */
const tokenize = (utterance: string): string[] =>
  (utterance.toLowerCase().replace(/([a-z])-(?=[a-z])/g, '$1 ').match(/\$\s*\d[\d,]*(?:\.\d+)?|[a-z0-9]+(?:[.,/'-][a-z0-9]+)*/g) ?? [])
    .map(token => token.replace(/\s+/g, ''));

const isWordNumberStart = (word: string | undefined) => word !== undefined && (word in UNITS || word in TEENS || word in TENS || word === 'point');

const digitNumberAt = (tokens: string[], index: number): { spoken: SpokenNumber; next: number } => {
  const token = tokens[index]!;
  const text = token.replace(/^\$/, '');
  const isMoney = token.startsWith('$') || MONEY_WORDS.has(tokens[index + 1] ?? '');
  return { spoken: { value: Number(text.replace(/,/g, '')), text, isMoney }, next: index + 1 };
};

/** Decimal digits spoken after `point` ("point five", "two point seven five"). */
const decimalDigitsAt = (tokens: string[], index: number): { fraction: number; next: number } => {
  let digits = '';
  let next = index;
  while (tokens[next]! in UNITS) {
    digits += UNITS[tokens[next]!];
    next += 1;
  }
  return { fraction: digits ? Number(`0.${digits}`) : 0, next };
};

/**
 * One spoken-number phrase starting at `index`. Adjacent number words combine only the way
 * English composes them (tens + unit, then hundred/thousand multipliers), so "two four foot"
 * reads as 2 and "five fifty" as 5 rather than summing into a number nobody said.
 */
const wordNumberAt = (tokens: string[], index: number): { spoken: SpokenNumber; next: number } | undefined => {
  let next = index;
  if (ARTICLES.has(tokens[next]!) && tokens[next + 1]! in MULTIPLIERS) next += 1;
  if (!isWordNumberStart(tokens[next]) && !(tokens[next]! in MULTIPLIERS && next > index)) return undefined;

  let total = 0;
  let group = next > index ? 1 : 0;
  let lastKind: 'unit' | 'teen' | 'tens' | 'multiplier' | undefined = next > index ? 'unit' : undefined;

  while (next < tokens.length) {
    const word = tokens[next]!;
    const canTakeSmall = lastKind === undefined || lastKind === 'multiplier';
    if (word in UNITS && (canTakeSmall || lastKind === 'tens')) {
      group += UNITS[word];
      lastKind = 'unit';
    } else if (word in TEENS && canTakeSmall) {
      group += TEENS[word];
      lastKind = 'teen';
    } else if (word in TENS && canTakeSmall) {
      group += TENS[word];
      lastKind = 'tens';
    } else if (word in MULTIPLIERS && lastKind !== undefined && lastKind !== 'multiplier') {
      const multiplier = MULTIPLIERS[word];
      if (multiplier === 1000) {
        total = (total + group) * 1000;
        group = 0;
      } else {
        group *= multiplier;
      }
      lastKind = 'multiplier';
    } else if (word === 'and' && lastKind === 'multiplier' && isWordNumberStart(tokens[next + 1])) {
      // "one hundred and five"
    } else {
      break;
    }
    next += 1;
  }

  let value = total + group;
  if (tokens[next] === 'point') {
    const decimal = decimalDigitsAt(tokens, next + 1);
    value += decimal.fraction;
    next = decimal.next;
  } else if (tokens[next] === 'and' && tokens[next + 1] === 'a' && tokens[next + 2] === 'half') {
    value += 0.5;
    next += 3;
  }

  if (next === index) return undefined;
  return { spoken: { value, isMoney: MONEY_WORDS.has(tokens[next] ?? '') }, next };
};

const isPronounOne = (tokens: string[], start: number, next: number) =>
  next === start + 1 && tokens[start] === 'one' && PRONOUN_ONE_DETERMINERS.has(tokens[start - 1] ?? '');

/** Every quantity or dollar amount in the utterance, in the order it was spoken. */
const spokenNumbers = (utterance: string): SpokenNumber[] => {
  const tokens = tokenize(utterance);
  const results: SpokenNumber[] = [];
  let index = 0;

  while (index < tokens.length) {
    const parsed = PLAIN_DIGITS.test(tokens[index]!) ? digitNumberAt(tokens, index) : wordNumberAt(tokens, index);
    if (!parsed) {
      index += 1;
      continue;
    }
    const negated = NEGATIONS.has(tokens[index - 1] ?? '');
    if (!negated && !isPronounOne(tokens, index, parsed.next) && !Number.isNaN(parsed.spoken.value)) {
      results.push(parsed.spoken);
    }
    index = parsed.next;
  }

  return results;
};

/** The first quantity spoken, skipping dollar amounts and digits that appear in a product label. */
export const firstSpokenNumber = (utterance: string, ignoringNumbersIn = ''): number | undefined => {
  const labelTokens = new Set(tokenize(ignoringNumbersIn));
  return spokenNumbers(utterance).find(number => !number.isMoney && !(number.text && labelTokens.has(number.text)))?.value;
};

/** The first dollar amount spoken in the utterance. */
export const firstSpokenMoney = (utterance: string): number | undefined => spokenNumbers(utterance).find(number => number.isMoney)?.value;

/** The number meant for a value of the given type. */
export const spokenAmountForValueType = (utterance: string, valueType: string): number | undefined =>
  valueType === 'usd'
    ? firstSpokenMoney(utterance) ?? firstSpokenNumber(utterance)
    : firstSpokenNumber(utterance);
